import {
    BORROWER_INFORMATION,
    CURRENT_ADDRESS,
    INITIAL_DETAILS,
    LENDER_INFORMATION,
    MAILING_INFO,
    PROPERTY_DETAILS,
    PURCHASER_INFORMATION,
    SELLER_INFORMATION,
} from '../constants/intakeSteps';
import type { ProspectMatterType } from '../enums';
import type { TemplateFormLanguage } from '../types/templateForms';
import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from './constants';

export interface StaticStepLabelsTranslations {
    steps: Record<string, string>;
    ordinals: string[];
    ordinalContactLabels: {
        seller: (ordinal: string) => string;
        purchaser: (ordinal: string) => string;
        lender: (ordinal: string) => string;
        user: (ordinal: string) => string;
    };
}

const ENGLISH_ORDINALS = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th'];

const FRENCH_ORDINALS = ['1er', '2e', '3e', '4e', '5e', '6e', '7e', '8e', '9e', '10e'];

const ENGLISH_TRANSLATIONS: StaticStepLabelsTranslations = {
    steps: {
        [INITIAL_DETAILS]: INITIAL_DETAILS,
        [SELLER_INFORMATION]: SELLER_INFORMATION,
        [PURCHASER_INFORMATION]: PURCHASER_INFORMATION,
        [BORROWER_INFORMATION]: BORROWER_INFORMATION,
        [LENDER_INFORMATION]: LENDER_INFORMATION,
        [MAILING_INFO]: MAILING_INFO,
        [CURRENT_ADDRESS]: CURRENT_ADDRESS,
        [PROPERTY_DETAILS]: PROPERTY_DETAILS,
    },
    ordinals: ENGLISH_ORDINALS,
    ordinalContactLabels: {
        seller: (ordinal) => `${ordinal} Seller info`,
        purchaser: (ordinal) => `${ordinal} Purchaser info`,
        lender: (ordinal) => `${ordinal} Lender info`,
        user: (ordinal) => `${ordinal} User info`,
    },
};

const FRENCH_TRANSLATIONS: StaticStepLabelsTranslations = {
    steps: {
        [INITIAL_DETAILS]: 'Détails initiaux',
        [SELLER_INFORMATION]: 'Vendeur(s)',
        [PURCHASER_INFORMATION]: 'Acheteur(s)',
        [BORROWER_INFORMATION]: 'Emprunteur(s)',
        [LENDER_INFORMATION]: 'Prêteur(s)',
        [MAILING_INFO]: 'Adresse postale',
        [CURRENT_ADDRESS]: 'Adresse actuelle',
        [PROPERTY_DETAILS]: 'Propriété',
    },
    ordinals: FRENCH_ORDINALS,
    ordinalContactLabels: {
        seller: (ordinal) => `${ordinal} vendeur`,
        purchaser: (ordinal) => `${ordinal} acheteur`,
        lender: (ordinal) => `${ordinal} prêteur`,
        user: (ordinal) => `${ordinal} utilisateur`,
    },
};

export const STATIC_STEP_LABELS: Record<TemplateFormLanguage, StaticStepLabelsTranslations> = {
    en: ENGLISH_TRANSLATIONS,
    fr: FRENCH_TRANSLATIONS,
};

export function getStaticStepLabelsTranslations(
    language: TemplateFormLanguage = DEFAULT_TEMPLATE_FORM_LANGUAGE,
): StaticStepLabelsTranslations {
    return STATIC_STEP_LABELS[language] ?? STATIC_STEP_LABELS.en;
}

export function getStaticStepLabel(
    stepKey: string,
    language: TemplateFormLanguage = DEFAULT_TEMPLATE_FORM_LANGUAGE,
): string {
    const translations = getStaticStepLabelsTranslations(language);
    return translations.steps[stepKey] ?? stepKey;
}

interface OrdinalContact {
    firstName?: string | null;
    lastName?: string | null;
    contactEmail?: string | null;
}

export function getOrdinalContactLabel(
    process: ProspectMatterType | undefined,
    index: number,
    language: TemplateFormLanguage = DEFAULT_TEMPLATE_FORM_LANGUAGE,
    contacts?: OrdinalContact[],
): string {
    const contact = contacts?.[index];

    if (contacts && contacts.length > 1) {
        if (contact?.firstName && contact.lastName && contact.contactEmail) {
            return `${contact.firstName} ${contact.lastName} (${contact.contactEmail})`;
        }
    }

    const translations = getStaticStepLabelsTranslations(language);
    const ordinal = translations.ordinals[index] ?? `${index + 1}`;

    switch (process) {
        case 'Sale':
            return translations.ordinalContactLabels.seller(ordinal);
        case 'Purchase':
            return translations.ordinalContactLabels.purchaser(ordinal);
        case 'PrivateLender':
            return translations.ordinalContactLabels.lender(ordinal);
        default:
            return translations.ordinalContactLabels.user(ordinal);
    }
}
